import { useQuery } from "@apollo/client";
import { useParams } from "@tanstack/react-router";
import { GET_ALL_PROJECTS, GET_USER } from "api/query";
import type { GetAllProjectsQuery, GetUserQuery } from "graphql/generated";
import { parseSelectedProjectId } from "utils/parseSelectedProjectId";
import { selectBestProjectId } from "utils/selectBestProjectId";

/**
 * The active projectId for building links/navigations.
 *
 * Prefers the URL param (the source of truth on project routes, which live
 * under `projectLayout` at `projects/{$projectId}`). Routes without a
 * projectId (account, joinProject, `/welcome`) fall back to the user's saved
 * selection in the cached `GET_USER`. When no selection is saved, for example
 * when the save of a new user's first project fails, they fall back to the
 * best project in the cached `GET_ALL_PROJECTS`, as the router does.
 *
 * The fallbacks read the cache only and never fetch, so signed-out pages do
 * not request `GET_USER` or `GET_ALL_PROJECTS`. The id is empty until one of
 * them is in the cache, which these consumers never render before.
 */
export const useProjectId = (): string => {
  const projectId = useParams({ strict: false, select: (p) => p.projectId });
  const { data: userData } = useQuery<GetUserQuery>(GET_USER, {
    fetchPolicy: "cache-only",
  });
  const knownProjectId =
    projectId ?? parseSelectedProjectId(userData?.getUser.uiState);
  // Read the list only when it is the last fallback. A cache miss costs one
  // more render, and almost all consumers have a projectId in the URL.
  const { data: projectsData } = useQuery<GetAllProjectsQuery>(
    GET_ALL_PROJECTS,
    { fetchPolicy: "cache-only", skip: knownProjectId !== undefined },
  );
  return (
    knownProjectId ??
    selectBestProjectId(projectsData?.getAllProjects ?? []) ??
    ""
  );
};
